<script setup lang="ts">
import { ref, computed } from 'vue'
import { router } from '@inertiajs/vue3'
import AppLayout from '@/Layouts/AppLayout.vue'
import { Button } from '@/Components/ui/button'
import { Badge } from '@/Components/ui/badge'
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'
import TutorsListingStatsCard from '@/Components/TutorsListingStatsCard.vue'
import TutorsListingFilterSort from '@/Components/TutorsListingFilterSort.vue'
import TutorCard from '@/Components/TutorCard.vue'

interface Tutor {
  id: number
  slug: string
  name: string
  avatar: string
  qualification: string
  rating: number
  reviews_count: number
  students_count: number
  subjects: Array<{ id: number; name: string; color: string }>
  levels: string[]
  examboards: string[]
  bio: string
  services_offered: string[]
  price_base: number
  price_silver: number
  price_gold: number
  teaching_experience?: string
  teaching_style?: string
}

interface Subject {
  id: number
  name: string
  slug: string
  color: string
  icon: string
}

interface Level {
  id: number
  name: string
  slug: string
}

interface Examboard {
  id: number
  name: string
  slug: string
}

interface Stats {
  videos: number
  groups: number
  one_on_one: number
  materials: number
  tutors_with_videos: number
  tutors_with_materials: number
}

interface Props {
  tutors: {
    data: Tutor[]
    current_page: number
    last_page: number
    per_page: number
    total: number
    from: number
    to: number
  }
  levels: Level[]
  subjects: Subject[]
  examboards: Examboard[]
  filters: {
    subject?: string
    subjects?: string[]
    levels?: string[]
    examboards?: string[]
    price_range?: string
    rating?: number
    min_students?: number
    teaching_methods?: string[]
    sort?: string
  }
  stats: Stats
}

const props = defineProps<Props>()

// Local state
const sortBy = ref(props.filters.sort || 'popular')
const viewMode = ref<'grid' | 'list'>('grid')
const activeServiceFilters = ref<string[]>(
  Array.isArray(props.filters.teaching_methods) ? props.filters.teaching_methods : []
)

// Initialize multi-select filter arrays from URL
const toArray = (val: any): string[] =>
  Array.isArray(val) ? val : (val ? [val] : [])

const activeSubjects = ref<string[]>(
  toArray(props.filters.subjects).concat(
    props.filters.subject && !toArray(props.filters.subjects).includes(props.filters.subject)
      ? [props.filters.subject]
      : []
  )
)
const activeLevels = ref<string[]>(toArray(props.filters.levels))
const activeExamboards = ref<string[]>(toArray(props.filters.examboards))

// Stats data from backend (cached)
const statsData = computed(() => ({
  videos: props.stats?.videos ?? 0,
  groups: props.stats?.groups ?? 0,
  one_on_one: props.stats?.one_on_one ?? 0,
  materials: props.stats?.materials ?? 0,
  tutors_with_videos: props.stats?.tutors_with_videos ?? 0,
  tutors_with_materials: props.stats?.tutors_with_materials ?? 0,
}))

// Map frontend service names to backend teaching method slugs
const serviceToSlugMap: Record<string, string> = {
  'videos': 'recorded-video-content',
  'materials': 'study-material',
  'group_sessions': 'group-tutoring',
  'one_on_one': '11-tutoring'
}

// Centralized method to build URL params from current filter state
const buildFilterParams = (): Record<string, any> => {
  const params: any = {}

  if (activeSubjects.value.length > 0) params.subjects = activeSubjects.value
  if (activeLevels.value.length > 0) params.levels = activeLevels.value
  if (activeExamboards.value.length > 0) params.examboards = activeExamboards.value
  if (activeServiceFilters.value.length > 0) params.teaching_methods = activeServiceFilters.value
  if (props.filters.price_range) params.price_range = props.filters.price_range
  if (props.filters.rating) params.rating = props.filters.rating
  if (props.filters.min_students) params.min_students = props.filters.min_students
  if (sortBy.value) params.sort = sortBy.value

  return params
}

// Navigate with filters
const navigateWithFilters = (preserveScroll = true) => {
  router.get('/tutors', buildFilterParams(), {
    preserveState: true,
    preserveScroll,
  })
}

// Service filter handler (supports multiple selections)
const handleServiceFilter = (service: string) => {
  const slug = serviceToSlugMap[service]

  // Toggle filter - add or remove from array
  const index = activeServiceFilters.value.indexOf(slug)
  if (index > -1) {
    activeServiceFilters.value.splice(index, 1)
  } else {
    activeServiceFilters.value.push(slug)
  }

  navigateWithFilters()
}

// Check if a service filter is active
const isServiceFilterActive = (service: string): boolean => {
  const slug = serviceToSlugMap[service]
  return activeServiceFilters.value.includes(slug)
}

// Filter and navigate
const handleFilterChange = (filters: { subjects: string[]; levels: string[]; examboards: string[] }) => {
  activeSubjects.value = filters.subjects
  activeLevels.value = filters.levels
  activeExamboards.value = filters.examboards
  navigateWithFilters()
}

const handleSortChange = (sort: string) => {
  sortBy.value = sort
  navigateWithFilters()
}

const handleViewChange = (view: 'grid' | 'list') => {
  viewMode.value = view
}


const changePage = (page: number) => {
  const params = { ...buildFilterParams(), page }

  router.get('/tutors', params, {
    preserveState: true,
    preserveScroll: false,
  })
}

</script>

<template>
  <AppLayout>
    <div class="container mx-auto mt-10 px-4 sm:px-6 lg:px-8">
      <!-- Stats Overview (Detailed View) -->
      <section class="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-6 mb-8">
        <TutorsListingStatsCard
          :count="statsData.videos"
          subtitle="Video Lessons"
          variant="video"
          view="detailed"
        />
        <TutorsListingStatsCard
          :count="statsData.groups"
          subtitle="Group Sessions"
          variant="group"
          view="detailed"
        />
        <TutorsListingStatsCard
          :count="statsData.one_on_one"
          subtitle="1:1 Tutors"
          variant="tutor"
          view="detailed"
        />
        <TutorsListingStatsCard
          :count="statsData.materials"
          subtitle="Study Materials"
          variant="material"
          view="detailed"
        />
      </section>

      <!-- Filter Buttons (Simple View) -->
      <section class="grid grid-cols-2 md:flex flex-nowrap overflow-x-scroll md:overflow-auto gap-3 mb-3 pb-3">
        <TutorsListingStatsCard
          :count="statsData.videos"
          subtitle="Videos"
          variant="video"
          view="simple"
          :is-active="isServiceFilterActive('videos')"
          @click="handleServiceFilter('videos')"
        />
        <TutorsListingStatsCard
          :count="statsData.groups"
          subtitle="Group Sessions"
          subtitleMobile="Groups"
          variant="group"
          view="simple"
          :is-active="isServiceFilterActive('group_sessions')"
          @click="handleServiceFilter('group_sessions')"
        />
        <TutorsListingStatsCard
          :count="statsData.one_on_one"
          subtitle="1:1 Tutoring"
          variant="tutor"
          view="simple"
          :is-active="isServiceFilterActive('one_on_one')"
          @click="handleServiceFilter('one_on_one')"
        />
        <TutorsListingStatsCard
          :count="statsData.materials"
          subtitle="Materials"
          variant="material"
          view="simple"
          :is-active="isServiceFilterActive('materials')"
          @click="handleServiceFilter('materials')"
        />
      </section>

      <!-- Filter & Sort Section -->
      <TutorsListingFilterSort
        :total-count="tutors.total"
        :initial-view="viewMode"
        :initial-sort="sortBy"
        :initial-subjects="activeSubjects"
        :initial-levels="activeLevels"
        :initial-examboards="activeExamboards"
        :subjects="subjects"
        :levels="levels"
        :examboards="examboards"
        @filter-change="handleFilterChange"
        @sort-change="handleSortChange"
        @view-change="handleViewChange"
      />

      <!-- Tutors Grid -->
      <section :class="['grid gap-6 mb-8', viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 lg:grid-cols-2']">
        <TutorCard
          v-for="(tutor, index) in tutors.data"
          :key="tutor.id"
          :tutor="tutor"
          :color-index="index"
          :view-mode="viewMode"
        />
      </section>

      <!-- Pagination -->
      <section v-if="tutors.last_page > 1" id="pagination" class="flex items-center justify-between mb-8">
        <p class="text-gray-600 text-sm">
          Showing {{ tutors.from }}-{{ tutors.to }} of {{ tutors.total }} tutors
        </p>

        <div class="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            :disabled="tutors.current_page === 1"
            @click="changePage(tutors.current_page - 1)"
          >
            <ChevronLeft class="w-4 h-4" />
          </Button>

          <template v-for="page in tutors.last_page" :key="page">
            <Button
              v-if="page === 1 || page === tutors.last_page || (page >= tutors.current_page - 1 && page <= tutors.current_page + 1)"
              :variant="page === tutors.current_page ? 'default' : 'outline'"
              size="sm"
              class="min-w-[40px]"
              @click="changePage(page)"
            >
              {{ page }}
            </Button>
            <span
              v-else-if="page === tutors.current_page - 2 || page === tutors.current_page + 2"
              class="text-gray-500 px-2"
            >
              ...
            </span>
          </template>

          <Button
            variant="outline"
            size="sm"
            :disabled="tutors.current_page === tutors.last_page"
            @click="changePage(tutors.current_page + 1)"
          >
            <ChevronRight class="w-4 h-4" />
          </Button>
        </div>
      </section>
    </div>
  </AppLayout>
</template>
